import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Flashcard, FlashcardProgress, Deck } from '../types'

interface FlashcardsContextType {
  flashcards: Flashcard[]
  decks: Deck[]
  progress: FlashcardProgress[]
  loading: boolean
  error: string | null
  // Methods
  addFlashcard: (flashcard: Omit<Flashcard, 'card_id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateFlashcard: (card_id: string, updates: Partial<Flashcard>) => Promise<void>
  deleteFlashcard: (card_id: string) => Promise<void>
  addDeck: (deck: Omit<Deck, 'deck_id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateDeck: (deck_id: string, updates: Partial<Deck>) => Promise<void>
  deleteDeck: (deck_id: string) => Promise<void>
  submitReview: (card_id: string, quality: number) => Promise<void>
  refreshData: () => Promise<void>
}

const FlashcardsContext = createContext<FlashcardsContextType | undefined>(undefined)

export const useFlashcards = () => {
  const context = useContext(FlashcardsContext)
  if (context === undefined) {
    throw new Error('useFlashcards must be used within a FlashcardsProvider')
  }
  return context
}

interface FlashcardsProviderProps {
  children: ReactNode
}

export const FlashcardsProvider: React.FC<FlashcardsProviderProps> = ({ children }) => {
  const [flashcards, setFlashcards] = useState<Flashcard[]>([])
  const [decks, setDecks] = useState<Deck[]>([])
  const [progress, setProgress] = useState<FlashcardProgress[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refreshData = async () => {
    setLoading(true)
    setError(null)

    try {
      // This would normally fetch from your API
      // For now, we'll use placeholder data
      const mockFlashcards: Flashcard[] = [
        {
          card_id: 'card-1',
          deck_id: 'deck-1',
          front_text: 'こんにちは',
          back_text: 'Hello',
          reading: 'konnichiwa',
          meaning: 'Greeting',
          examples: ['こんにちは、世界！'],
          tags: ['greeting', 'basic'],
          difficulty: 'N5',
          card_type: 'vocabulary',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ]

      const mockDecks: Deck[] = [
        {
          deck_id: 'deck-1',
          user_id: 'user-1',
          name: 'Japanese Basics',
          description: 'Basic Japanese vocabulary',
          category: 'N5',
          card_count: 10,
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ]

      const mockProgress: FlashcardProgress[] = [
        {
          user_id: 'user-1',
          card_id: 'card-1',
          ease_factor: 2.5,
          interval: 1,
          repetitions: 0,
          last_review: undefined,
          next_review: '2024-01-02T00:00:00Z',
          created_at: '2024-01-01T00:00:00Z',
          updated_at: '2024-01-01T00:00:00Z'
        }
      ]

      setFlashcards(mockFlashcards)
      setDecks(mockDecks)
      setProgress(mockProgress)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const addFlashcard = async (flashcard: Omit<Flashcard, 'card_id' | 'created_at' | 'updated_at'>) => {
    setLoading(true)
    try {
      // Mock API call
      const newFlashcard: Flashcard = {
        ...flashcard,
        card_id: `card-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      setFlashcards(prev => [...prev, newFlashcard])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add flashcard')
    } finally {
      setLoading(false)
    }
  }

  const updateFlashcard = async (card_id: string, updates: Partial<Flashcard>) => {
    setLoading(true)
    try {
      setFlashcards(prev => prev.map(card =>
        card.card_id === card_id
          ? { ...card, ...updates, updated_at: new Date().toISOString() }
          : card
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update flashcard')
    } finally {
      setLoading(false)
    }
  }

  const deleteFlashcard = async (card_id: string) => {
    setLoading(true)
    try {
      setFlashcards(prev => prev.filter(card => card.card_id !== card_id))
      setProgress(prev => prev.filter(p => p.card_id !== card_id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete flashcard')
    } finally {
      setLoading(false)
    }
  }

  const addDeck = async (deck: Omit<Deck, 'deck_id' | 'created_at' | 'updated_at'>) => {
    setLoading(true)
    try {
      const newDeck: Deck = {
        ...deck,
        deck_id: `deck-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      setDecks(prev => [...prev, newDeck])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add deck')
    } finally {
      setLoading(false)
    }
  }

  const updateDeck = async (deck_id: string, updates: Partial<Deck>) => {
    setLoading(true)
    try {
      setDecks(prev => prev.map(deck =>
        deck.deck_id === deck_id
          ? { ...deck, ...updates, updated_at: new Date().toISOString() }
          : deck
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update deck')
    } finally {
      setLoading(false)
    }
  }

  const deleteDeck = async (deck_id: string) => {
    setLoading(true)
    try {
      setDecks(prev => prev.filter(deck => deck.deck_id !== deck_id))
      setFlashcards(prev => prev.filter(card => card.deck_id !== deck_id))
      setProgress(prev => prev.filter(p => flashcards.find(f => f.deck_id === deck_id)?.card_id === p.card_id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete deck')
    } finally {
      setLoading(false)
    }
  }

  const submitReview = async (card_id: string, quality: number) => {
    setLoading(true)
    try {
      // Mock SM-2 algorithm implementation
      setProgress(prev => prev.map(p =>
        p.card_id === card_id
          ? {
              ...p,
              ease_factor: Math.max(1.3, p.ease_factor + (quality >= 3 ? 0.1 : -0.2)),
              interval: quality >= 3 ? p.interval * p.ease_factor : 1,
              repetitions: quality >= 3 ? p.repetitions + 1 : 0,
              last_review: new Date().toISOString(),
              next_review: new Date(Date.now() + (quality >= 3 ? p.interval * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000)).toISOString(),
              updated_at: new Date().toISOString()
            }
          : p
      ))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit review')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshData()
  }, [])

  const value: FlashcardsContextType = {
    flashcards,
    decks,
    progress,
    loading,
    error,
    addFlashcard,
    updateFlashcard,
    deleteFlashcard,
    addDeck,
    updateDeck,
    deleteDeck,
    submitReview,
    refreshData
  }

  return (
    <FlashcardsContext.Provider value={value}>
      {children}
    </FlashcardsContext.Provider>
  )
}